import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { LabelsTable } from '@mint-lib/labels-table';

import type { Label } from '../../api/users/users.types.js';
import ConditionalSkeleton from '../../atoms/ConditionalSkeleton/ConditionalSkeleton.jsx';
import type { UserModalType } from '../../organisms/UserModalNew/validation.js';

type Props = {
  formInstance: UseFormReturn<UserModalType>;
  isReadonlyPermission: boolean;
  initialTags: Label[] | null;
  isSaving: boolean;
  isTagsLoading?: boolean;
};
type AssignedTagsMapType = Record<string, { id: string }[]>;

const LabelsHierarchy = ({
  formInstance,
  isReadonlyPermission,
  initialTags,
  isSaving,
  isTagsLoading = false,
}: Props) => {
  const initialAssignedTagsMap =
    initialTags?.reduce<AssignedTagsMapType>((acc, { id, categoryType }) => {
      if (categoryType) {
        if (!acc[categoryType]) {
          acc[categoryType] = [];
        }
        acc[categoryType].push({ id: id ?? '' });
        return acc;
      }
      return acc;
    }, {} as AssignedTagsMapType) ?? null;

  const [assignedTagsMap, setAssignedTagsMap] =
    useState<AssignedTagsMapType | null>(initialAssignedTagsMap);
  const [categoryType, setCategoryType] = useState<string | null>(null);

  const assignedTags =
    categoryType && assignedTagsMap?.[categoryType]
      ? assignedTagsMap[categoryType]
      : initialTags?.map((tag) => {
          return { id: tag.id ?? '' };
        });

  const handleCategoryChange = (categoryType: string) => {
    setCategoryType(categoryType);
  };

  const handleTagsAssign = (tags: { id: string }[]) => {
    setAssignedTagsMap({
      ...assignedTagsMap,
      [categoryType || '']: tags,
    });

    const newtags = {
      ...assignedTagsMap,
      [categoryType || '']: tags,
    };
    formInstance.setValue(
      'tags',
      Object.values(newtags ?? {}).flatMap((arr) => arr.map((item) => item.id)),
      {
        shouldDirty: true,
      },
    );
    formInstance.trigger('tags');
  };

  return (
    // Without this the panel would render an empty label list while the user's
    // labels are still in flight, which reads as "this user has none".
    <ConditionalSkeleton isLoading={isTagsLoading} className="h-full w-full">
      {initialTags && (
        <LabelsTable
          isReadonlyPermission={isReadonlyPermission}
          isSaving={isSaving}
          isMint2
          assignedTags={assignedTags || []}
          onTagsAssign={handleTagsAssign}
          onCategoryChange={handleCategoryChange}
        />
      )}
    </ConditionalSkeleton>
  );
};

export default LabelsHierarchy;
